import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    TextField,
    MenuItem,
    Grid,
    FormControlLabel,
    Switch,
    IconButton,
    Tooltip,
    Snackbar,
    Divider,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    AccessTime as TimeIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface TimeSlot {
    startTime: string;
    endTime: string;
    maxBookings: number;
    slotDurationMinutes?: number;
    bufferMinutes?: number;
}

interface DiarySchema {
    id: string;
    diaryId: string;
    name: string;
    description?: string;
    validFrom: string;
    validTo?: string;
    dayOfWeek: number;
    timeSlots: TimeSlot[];
    active: boolean;
    created_at?: string;
    updated_at?: string;
}

interface SchemaForm {
    name: string;
    description: string;
    validFrom: string;
    validTo: string;
    dayOfWeek: number;
    timeSlots: TimeSlot[];
    active: boolean;
}

interface DiarySchemasDialogProps {
    open: boolean;
    onClose: () => void;
    diaryId: string;
    diaryName: string;
}

const DAYS_OF_WEEK = [
    { value: 1, label: 'Lunedì', short: 'LUN' },
    { value: 2, label: 'Martedì', short: 'MAR' },
    { value: 3, label: 'Mercoledì', short: 'MER' },
    { value: 4, label: 'Giovedì', short: 'GIO' },
    { value: 5, label: 'Venerdì', short: 'VEN' },
    { value: 6, label: 'Sabato', short: 'SAB' },
    { value: 7, label: 'Domenica', short: 'DOM' }
];

const DiarySchemasDialog: React.FC<DiarySchemasDialogProps> = ({
    open,
    onClose,
    diaryId,
    diaryName
}) => {
    const [schemas, setSchemas] = useState<DiarySchema[]>([]);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingSchema, setEditingSchema] = useState<DiarySchema | null>(null);
    
    // Form state
    const [form, setForm] = useState<SchemaForm>({
        name: '',
        description: '',
        validFrom: new Date().toISOString().split('T')[0],
        validTo: '',
        dayOfWeek: 1, // Lunedì
        timeSlots: [
            { 
                startTime: '08:00', 
                endTime: '10:00', 
                maxBookings: 10,
                slotDurationMinutes: 30,
                bufferMinutes: 0
            }
        ],
        active: true
    });
    
    // Snackbar
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    // Load schemas when dialog opens
    useEffect(() => {
        if (open && diaryId) {
            loadSchemas();
        }
    }, [open, diaryId]);

    const loadSchemas = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/diary-schemas/${diaryId}`);
            if (response.data.success) {
                // Assicuriamo che schemas sia sempre un array
                const schemasData = response.data.data;
                if (Array.isArray(schemasData)) {
                    setSchemas(schemasData);
                } else {
                    console.warn('Backend returned non-array data for schemas:', schemasData);
                    setSchemas([]);
                }
            } else {
                setError(response.data.error || 'Errore nel caricamento degli schemi agenda');
                setSchemas([]);  // Garantisce che schemas sia sempre un array
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || 'Errore nel caricamento degli schemi agenda';
            console.error('Error loading diary schemas:', errorMessage);
            setError(`Impossibile caricare gli schemi agenda: ${errorMessage}`);
            setSchemas([]);  // Garantisce che schemas sia sempre un array anche in caso di errore
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSchema = async () => {
        // Validazione
        if (!form.name.trim()) {
            setSnackbar({ 
                open: true, 
                message: 'Il nome dello schema è obbligatorio', 
                severity: 'error' 
            });
            return;
        }

        if (form.timeSlots.length === 0) {
            setSnackbar({ 
                open: true, 
                message: 'Almeno un time slot è obbligatorio', 
                severity: 'error' 
            });
            return;
        }

        // Validazione time slots
        for (let i = 0; i < form.timeSlots.length; i++) {
            const slot = form.timeSlots[i];
            if (!slot.startTime || !slot.endTime || !slot.maxBookings) {
                setSnackbar({ 
                    open: true, 
                    message: `Time slot ${i + 1}: tutti i campi sono obbligatori`, 
                    severity: 'error' 
                });
                return;
            }
            
            if (slot.startTime >= slot.endTime) {
                setSnackbar({ 
                    open: true, 
                    message: `Time slot ${i + 1}: orario inizio deve essere precedente all'orario fine`, 
                    severity: 'error' 
                });
                return;
            }
        }

        setFormLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                validFrom: form.validFrom,
                validTo: form.validTo || undefined,
                dayOfWeek: form.dayOfWeek,
                timeSlots: form.timeSlots,
                active: form.active
            };

            const response = await apiClient.post(`/diary-schemas/${diaryId}/create-recup`, payload);
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Schema agenda creato con successo su RECUP!', 
                    severity: 'success' 
                });
                
                resetForm();
                setShowForm(false);
                await loadSchemas(); // Reload list
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nella creazione dello schema', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nella creazione dello schema', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleUpdateSchema = async () => {
        if (!editingSchema) return;

        setFormLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                validFrom: form.validFrom,
                validTo: form.validTo || undefined,
                dayOfWeek: form.dayOfWeek,
                timeSlots: form.timeSlots,
                active: form.active
            };

            const response = await apiClient.put(
                `/diary-schemas/${diaryId}/${editingSchema.id}/update-recup`, 
                payload
            );
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Schema agenda aggiornato con successo su RECUP!', 
                    severity: 'success' 
                });
                
                resetForm();
                setShowForm(false);
                setEditingSchema(null);
                await loadSchemas(); // Reload list
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'aggiornamento dello schema', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'aggiornamento dello schema', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteSchema = async (schema: DiarySchema) => {
        const confirmed = window.confirm(
            `Sei sicuro di voler eliminare lo schema "${schema.name}"?\n\n` +
            `Giorno: ${getDayName(schema.dayOfWeek)}\n` +
            `Time slots: ${schema.timeSlots?.length || 0}\n\n` +
            `L'eliminazione avverrà anche su RECUP.`
        );
        
        if (!confirmed) return;

        try {
            const response = await apiClient.delete(
                `/diary-schemas/${diaryId}/${schema.id}/delete-recup`,
                {
                    data: { reason: 'Eliminazione schema richiesta dall\'utente' }
                }
            );
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Schema eliminato con successo da RECUP!', 
                    severity: 'success' 
                });
                await loadSchemas(); // Reload list
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'eliminazione dello schema', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'eliminazione dello schema', 
                severity: 'error' 
            });
        }
    };

    const handleEditSchema = (schema: DiarySchema) => {
        setEditingSchema(schema);
        setForm({
            name: schema.name,
            description: schema.description || '',
            validFrom: schema.validFrom ? schema.validFrom.split('T')[0] : new Date().toISOString().split('T')[0],
            validTo: schema.validTo ? schema.validTo.split('T')[0] : '',
            dayOfWeek: schema.dayOfWeek,
            timeSlots: schema.timeSlots || [{ 
                startTime: '08:00', 
                endTime: '10:00', 
                maxBookings: 10,
                slotDurationMinutes: 30,
                bufferMinutes: 0
            }],
            active: schema.active
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setForm({
            name: '',
            description: '',
            validFrom: new Date().toISOString().split('T')[0],
            validTo: '',
            dayOfWeek: 1,
            timeSlots: [
                { 
                    startTime: '08:00', 
                    endTime: '10:00', 
                    maxBookings: 10,
                    slotDurationMinutes: 30,
                    bufferMinutes: 0
                }
            ],
            active: true
        });
        setEditingSchema(null);
    };

    const addTimeSlot = () => {
        setForm(prev => ({
            ...prev,
            timeSlots: [
                ...prev.timeSlots,
                { 
                    startTime: '14:00', 
                    endTime: '16:00', 
                    maxBookings: 10,
                    slotDurationMinutes: 30,
                    bufferMinutes: 0
                }
            ]
        }));
    };

    const removeTimeSlot = (index: number) => {
        if (form.timeSlots.length > 1) {
            setForm(prev => ({
                ...prev,
                timeSlots: prev.timeSlots.filter((_, i) => i !== index)
            }));
        }
    };

    const updateTimeSlot = (index: number, field: keyof TimeSlot, value: any) => {
        setForm(prev => ({
            ...prev,
            timeSlots: prev.timeSlots.map((slot, i) => 
                i === index ? { ...slot, [field]: value } : slot
            )
        }));
    };

    const getDayName = (dayOfWeek: number) => {
        return DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || `Giorno ${dayOfWeek}`;
    };

    const getDayShort = (dayOfWeek: number) => {
        return DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.short || `${dayOfWeek}`;
    };

    const formatTime = (time: string) => {
        return time;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '80vh' }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ScheduleIcon />
                            <Typography variant="h6">
                                Schemi/Fasce Agenda: {diaryName}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Statistiche */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Totale Schemi
                                    </Typography>
                                    <Typography variant="h4">
                                        {schemas.length}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Schemi Attivi
                                    </Typography>
                                    <Typography variant="h4" color="success.main">
                                        {Array.isArray(schemas) ? schemas.filter(s => s.active).length : 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Giorni Coperti
                                    </Typography>
                                    <Typography variant="h4" color="info.main">
                                        {Array.isArray(schemas) ? new Set(schemas.filter(s => s.active).map(s => s.dayOfWeek)).size : 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography color="textSecondary" gutterBottom>
                                        Totale Time Slots
                                    </Typography>
                                    <Typography variant="h4" color="warning.main">
                                        {Array.isArray(schemas) ? schemas.reduce((acc, s) => acc + (s.timeSlots?.length || 0), 0) : 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Controlli */}
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                resetForm();
                                setShowForm(!showForm);
                            }}
                            color={showForm ? "secondary" : "primary"}
                        >
                            {showForm ? 'Nascondi Form' : 'Nuovo Schema'}
                        </Button>
                        
                        <Button
                            variant="outlined" 
                            onClick={loadSchemas}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : undefined}
                        >
                            Ricarica
                        </Button>
                    </Box>

                    {/* Form Creazione/Modifica Schema */}
                    {showForm && (
                        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {editingSchema ? `Modifica Schema: ${editingSchema.name}` : 'Crea Nuovo Schema su RECUP'}
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nome Schema *"
                                            value={form.name}
                                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Es: Mattino LUN, Pomeriggio VEN..."
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Giorno Settimana *"
                                            value={form.dayOfWeek}
                                            onChange={(e) => setForm(prev => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                                            required
                                        >
                                            {DAYS_OF_WEEK.map((day) => (
                                                <MenuItem key={day.value} value={day.value}>
                                                    {day.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={form.active}
                                                    onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                                />
                                            }
                                            label="Schema Attivo"
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            label="Descrizione (opzionale)"
                                            value={form.description}
                                            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Descrizione dettagliata dello schema..."
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Valido Da *"
                                            value={form.validFrom}
                                            onChange={(e) => setForm(prev => ({ ...prev, validFrom: e.target.value }))}
                                            InputLabelProps={{ shrink: true }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Valido Fino (opzionale)"
                                            value={form.validTo}
                                            onChange={(e) => setForm(prev => ({ ...prev, validTo: e.target.value }))}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                {/* Time Slots */}
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h6">
                                            Time Slots (Fasce Orarie)
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={addTimeSlot}
                                        >
                                            Aggiungi Fascia
                                        </Button>
                                    </Box>

                                    {form.timeSlots.map((slot, index) => (
                                        <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                    <Typography variant="subtitle2">
                                                        Fascia {index + 1}
                                                    </Typography>
                                                    {form.timeSlots.length > 1 && (
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => removeTimeSlot(index)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6} md={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="time"
                                                            label="Inizio *"
                                                            value={slot.startTime}
                                                            onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            required
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <TextField
                                                            fullWidth
                                                            type="time"
                                                            label="Fine *"
                                                            value={slot.endTime}
                                                            onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                                                            InputLabelProps={{ shrink: true }}
                                                            required
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Max Prenotazioni *"
                                                            value={slot.maxBookings}
                                                            onChange={(e) => updateTimeSlot(index, 'maxBookings', parseInt(e.target.value) || 1)}
                                                            inputProps={{ min: 1, max: 100 }}
                                                            required
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Durata Slot (min)"
                                                            value={slot.slotDurationMinutes || 30}
                                                            onChange={(e) => updateTimeSlot(index, 'slotDurationMinutes', parseInt(e.target.value) || 30)}
                                                            inputProps={{ min: 5, max: 120 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            label="Buffer (min)"
                                                            value={slot.bufferMinutes || 0}
                                                            onChange={(e) => updateTimeSlot(index, 'bufferMinutes', parseInt(e.target.value) || 0)}
                                                            inputProps={{ min: 0, max: 60 }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={editingSchema ? handleUpdateSchema : handleCreateSchema}
                                        disabled={formLoading || !form.name.trim() || form.timeSlots.length === 0}
                                        startIcon={formLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                                    >
                                        {formLoading 
                                            ? (editingSchema ? 'Aggiornamento...' : 'Creazione...') 
                                            : (editingSchema ? 'Aggiorna su RECUP' : 'Crea su RECUP')
                                        }
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setShowForm(false);
                                            resetForm();
                                        }}
                                        startIcon={<CancelIcon />}
                                    >
                                        Annulla
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Lista Schemi */}
                    <Typography variant="h6" gutterBottom>
                        Schemi Esistenti
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : schemas.length === 0 ? (
                        <Alert severity="info">
                            Nessun schema trovato per questa agenda.
                        </Alert>
                    ) : (
                        <Box>
                            {schemas.map((schema) => (
                                <Accordion key={schema.id} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                                {schema.name}
                                            </Typography>
                                            <Chip 
                                                label={getDayShort(schema.dayOfWeek)} 
                                                color="primary" 
                                                size="small" 
                                            />
                                            <Chip 
                                                label={`${schema.timeSlots?.length || 0} fasce`} 
                                                color="info" 
                                                size="small" 
                                            />
                                            <Chip 
                                                label={schema.active ? "ATTIVO" : "INATTIVO"} 
                                                color={schema.active ? "success" : "default"} 
                                                size="small" 
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Giorno:</strong> {getDayName(schema.dayOfWeek)}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Valido:</strong> {formatDate(schema.validFrom)}
                                                    {schema.validTo && ` - ${formatDate(schema.validTo)}`}
                                                </Typography>
                                                {schema.description && (
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        <strong>Descrizione:</strong> {schema.description}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    Time Slots:
                                                </Typography>
                                                <List dense>
                                                    {(schema.timeSlots || []).map((slot, index) => (
                                                        <ListItem key={index} disablePadding>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <TimeIcon fontSize="small" />
                                                                        <Typography variant="body2">
                                                                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                                                        </Typography>
                                                                        <Chip 
                                                                            label={`max ${slot.maxBookings}`} 
                                                                            size="small" 
                                                                            variant="outlined"
                                                                        />
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    slot.slotDurationMinutes && (
                                                                        `Durata: ${slot.slotDurationMinutes}min, Buffer: ${slot.bufferMinutes || 0}min`
                                                                    )
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Grid>
                                        </Grid>
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                            <Tooltip title="Modifica schema">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleEditSchema(schema)}
                                                >
                                                    Modifica
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Elimina schema">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => handleDeleteSchema(schema)}
                                                >
                                                    Elimina
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        Chiudi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar per notifiche */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DiarySchemasDialog;